import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  AnchorLink,
  ButtonLink,
  LoadingSuspense,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo } from "react";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";
import { useZitadelApplications } from "@/hooks/useZitadelApplications.ts";

export const Route = createFileRoute("/admin/clients/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Clients`,
      },
    ],
  }),
});

const ClientList: FC = () => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(
      () =>
        api
          .from("client")
          .select("id::text,name,owned_by,project:project_id(identifier,name)")
          .order("name"),
      [],
    ),
  );
  const { forClientId } = useZitadelApplications();

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Name</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Client ID</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Owned by</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Project</Trans>
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((d) => {
          const application = forClientId(d.id);
          return (
            <Table.Tr key={d.id}>
              <Table.Td>
                <AnchorLink to="/admin/clients/$id" params={{ id: d.id }}>
                  {d.name}
                </AnchorLink>
              </Table.Td>
              <Table.Td>
                <Text ff="monospace" size="sm">
                  {d.id}
                </Text>
              </Table.Td>
              <Table.Td>{d.owned_by ?? "-"}</Table.Td>
              <Table.Td>
                {d.project
                  ? `${d.project.identifier} (${d.project.name})`
                  : "-"}
              </Table.Td>
              <Table.Td>
                {application ? (
                  <Anchor
                    href={application.console_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Trans>View in Zitadel</Trans>
                  </Anchor>
                ) : (
                  <Tooltip
                    label={
                      <Trans>
                        No matching application found in Zitadel for this client
                        ID
                      </Trans>
                    }
                  >
                    <Badge color="gray" variant="light">
                      <Trans>Not found in Zitadel</Trans>
                    </Badge>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Breadcrumbs>
          <Text>
            <Trans>Admin</Trans>
          </Text>
          <Text>
            <Trans>Clients</Trans>
          </Text>
        </Breadcrumbs>
        <ButtonLink
          to="/admin/clients/create"
          leftSection={<FontAwesomeIcon icon={faPlus} />}
        >
          <Trans>Create</Trans>
        </ButtonLink>
      </Group>
      <LoadingSuspense>
        <ClientList />
      </LoadingSuspense>
    </Stack>
  );
}
